import React from "react"
import { Keyboard, View } from "react-native"
import {
  BFTextInput,
  type BFTextInputProps,
} from "@/components/standard/BFTextInput"
import { cn } from "@/components/util/cn"
import { BFColors } from "@/constants/Colors"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import BFTag from "@/components/standard/BFTag"

interface BFTaggedArrayInputProps extends BFTextInputProps {
  tags: string[] // Array of tags to display
  onDone?: (firstItem: string | null) => void
  onRemoveTag?: (tag: string) => void // Callback for removing a tag
}

const BFTaggedArrayInput: React.FC<BFTaggedArrayInputProps> = ({
  tags,
  onDone,
  onRemoveTag,
  variant = "form", // Default to "form" variant
  className,
  containerClassName,
  inputClassName,
  accessoryLeft,
  accessoryRight,
  ...props
}) => {
  const hasTags = tags.length > 0

  const handleDone = () => {
    const firstItem = tags.length > 0 ? tags[0] : null
    if (onDone) onDone(firstItem)
    Keyboard.dismiss() // Close the keyboard
  }

  return (
    <View className={hasTags ? "bg-lightest" : ""}>
      {/* Container for input and tags */}
      <Vertical
        style={{ minHeight: hasTags ? 90 : 50 }}
        className={cn(
          "flex-wrap items-start rounded border border-light-grey pt-3",
          hasTags ? "border-light-grey" : "border-transparent",
          containerClassName
        )}
      >
        {/* Render Tags */}
        {hasTags && (
          <Horizontal className="flex-wrap px-1 mb-1">
            {tags.map((tag) => (
              <BFTag
                key={tag}
                id={tag}
                title={tag}
                textColor={BFColors.darkGrey}
                onRemove={onRemoveTag}
              />
            ))}
          </Horizontal>
        )}

        <BFTextInput
          variant={variant}
          placeholder={hasTags ? "Add another ZIP Code" : props.placeholder}
          style={{ minHeight: 50 }}
          className={cn(
            "flex-1",
            hasTags ? "bg-lightest border-0" : "border-light-grey bg-lightest",
            className
          )}
          accessoryLeft={accessoryLeft}
          accessoryRight={accessoryRight}
          {...props}
          autoComplete="off"
          autoCorrect={false}
          contextMenuHidden={true}
          caretHidden={false} // Keeps the cursor visible
          keyboardType="number-pad"
          returnKeyType="done"
          onSubmitEditing={handleDone}
        />
      </Vertical>
    </View>
  )
}

export default BFTaggedArrayInput
